import { useEffect, useState, useRef } from "react";
import moment from "moment";
import "../styles/css/Worldpay.css";

const BRAND_GREEN = "#17BA17";

/**
 * Polished Worldpay Access Checkout modal.
 *
 * Renders an order summary, the Worldpay iframe-backed card fields, and a
 * Pay button. Closes on backdrop click or Esc, traps the user with an
 * overlay while the transaction authorizes.
 */
export default function WorldPayModal({
  closeModal,
  triggerAPI,
  message,
  isAPIProcessing,
  amount = 0,
  subtotal = 0,
  discount = 0,
  couponCode = "",
  teeTime,
  playerCount,
  playerName,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState("");
  const [formValid, setFormValid] = useState(false);
  const [cardHolder, setCardHolder] = useState(playerName || "");

  // We need handleSubmit to see the latest cardHolder; keep it in a ref.
  const cardHolderRef = useRef(cardHolder);
  useEffect(() => {
    cardHolderRef.current = cardHolder;
  }, [cardHolder]);

  const isBusy = isLoading || isAPIProcessing;

  // Esc to close (when not busy)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !isBusy) closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeModal, isBusy]);

  useEffect(() => {
    const loadWorldpay = () => {
      return new Promise((resolve, reject) => {
        if (window.Worldpay) {
          resolve(window.Worldpay);
          return;
        }
        const script = document.createElement("script");
        // Environment-driven so the same code runs against WorldPay test
        // (try.access) in dev and LIVE (access) in production. Defaults to
        // test so a build without the flag can never take real payments.
        // Production MUST set REACT_APP_WORLDPAY_ENV=live AND a live
        // REACT_APP_WORLDPAY_CHECKOUT_ID (identity) from the same account —
        // a mismatch is what causes WorldPay's "Identity is invalid" error.
        const wpBase =
          process.env.REACT_APP_WORLDPAY_ENV === "live"
            ? "https://access.worldpay.com"
            : "https://try.access.worldpay.com";
        script.src = wpBase + "/access-checkout/v2/checkout.js";
        script.async = true;
        script.onload = () => resolve(window.Worldpay);
        script.onerror = () => reject("Worldpay SDK failed to load");
        document.body.appendChild(script);
      });
    };

    loadWorldpay()
      .then((Worldpay) => {
        if (!Worldpay) {
          setSdkError("Payment service unavailable. Please try again.");
          return;
        }
        const form = document.getElementById("card-form");
        const clear = document.getElementById("clear");
        if (!form) return;

        Worldpay.checkout.init(
          {
            id: process.env.REACT_APP_WORLDPAY_CHECKOUT_ID,
            form: "#card-form",
            fields: {
              pan: { selector: "#card-pan" },
              expiry: { selector: "#card-expiry" },
              cvv: { selector: "#card-cvv" },
            },
            styles: {
              input: {
                color: "#111827",
                "font-weight": "500",
                "font-size": "16px",
                "letter-spacing": "1px",
                "font-family":
                  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              },
              "input.is-valid": { color: "#15803d" },
              "input.is-invalid": { color: "#b91c1c" },
              "input.is-onfocus": { color: "#111827" },
            },
            accessibility: {
              ariaLabel: {
                pan: "Card number",
                expiry: "Card expiry date",
                cvv: "Card security code",
              },
              lang: { locale: "en-GB" },
              title: {
                enabled: true,
                pan: "Card number",
                expiry: "Card expiry (MM/YY)",
                cvv: "Card security code",
              },
            },
            acceptedCardBrands: [
              "amex",
              "diners",
              "discover",
              "jcb",
              "maestro",
              "mastercard",
              "visa",
            ],
            enablePanFormatting: true,
          },
          (error, checkout) => {
            if (error) {
              console.error(error);
              setSdkError("Could not initialise card form. Please try again.");
              return;
            }
            setSdkReady(true);

            form.addEventListener("wp:form:change", (e) => {
              setFormValid(!!(e && e.detail && e.detail["is-valid"]));
            });

            form.addEventListener("submit", (event) => {
              event.preventDefault();
              if (!cardHolderRef.current || cardHolderRef.current.trim().length < 2) {
                setSdkError("Please enter the cardholder name.");
                return;
              }
              setSdkError("");
              checkout.generateSessionState((err, sessionState) => {
                if (err) {
                  console.error(err);
                  setSdkError(
                    "Card details look invalid. Please check and retry."
                  );
                  return;
                }
                handleSubmit(sessionState, cardHolderRef.current.trim());
              });
            });

            if (clear) {
              clear.addEventListener("click", (event) => {
                event.preventDefault();
                checkout.clearForm(() => {});
              });
            }
          }
        );
      })
      .catch((err) => {
        console.error("SDK Load Error:", err);
        setSdkError("Payment service is currently unavailable.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (sessionState, cardHolderName) => {
    setIsLoading(true);
    try {
      await triggerAPI(sessionState, cardHolderName);
    } finally {
      setIsLoading(false);
    }
  };

  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isBusy) closeModal();
  };

  const fmt = (n) => `£${(Number(n) || 0).toFixed(2)}`;
  const teeTimeLabel = teeTime
    ? moment(teeTime).format("dddd, Do MMMM YYYY")
    : "";

  return (
    <div className="wpBackdrop" onClick={onBackdropClick} role="presentation">
      <div
        className="wpModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wpTitle"
      >
        {/* Header */}
        <div className="wpHeader">
          <div className="wpHeaderText">
            <div className="wpBrandRow">
              <span className="wpLockIcon" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 10V8a6 6 0 1112 0v2m-9 0h6a3 3 0 013 3v6a3 3 0 01-3 3H9a3 3 0 01-3-3v-6a3 3 0 013-3z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>Secure Checkout</span>
            </div>
            <h2 id="wpTitle">Thorney Park Golf Club</h2>
          </div>
          <button
            className="wpCloseBtn"
            onClick={closeModal}
            disabled={isBusy}
            aria-label="Close payment form"
            type="button"
          >
            ✕
          </button>
        </div>

        <div className="wpBody">
          {/* Order summary */}
          <section className="wpSummary" aria-label="Order summary">
            <div className="wpSummaryTitle">Order summary</div>
            {teeTimeLabel && (
              <div className="wpSummaryRow">
                <span>Tee time</span>
                <strong>{teeTimeLabel}</strong>
              </div>
            )}
            {playerCount && (
              <div className="wpSummaryRow">
                <span>Players</span>
                <strong>{playerCount}</strong>
              </div>
            )}
            <div className="wpSummaryRow">
              <span>Subtotal</span>
              <strong>{fmt(subtotal)}</strong>
            </div>
            {discount > 0 && (
              <div className="wpSummaryRow wpDiscount">
                <span>
                  Discount{couponCode ? ` (${couponCode})` : ""}
                </span>
                <strong>−{fmt(discount)}</strong>
              </div>
            )}
            <div className="wpDivider" />
            <div className="wpSummaryRow wpTotal">
              <span>Total to pay</span>
              <strong>{fmt(amount)}</strong>
            </div>
          </section>

          {/* Card brand row */}
          <div className="wpCardBrands" aria-hidden="true">
            <CardBadge text="VISA" />
            <CardBadge text="MC" />
            <CardBadge text="AMEX" />
            <CardBadge text="Maestro" />
          </div>

          {/* Worldpay form */}
          <div className="wpFormWrapper">
            {!sdkReady && !sdkError && (
              <div className="wpFormSkeleton" aria-hidden="true">
                <div className="wpSpinner" />
                <p>Loading secure form…</p>
              </div>
            )}

            <form
              className="wpForm"
              id="card-form"
              style={{ display: sdkReady ? "block" : "none" }}
            >
              <div className="wpField">
                <label className="wpLabel" htmlFor="wpCardHolder">
                  Cardholder name
                </label>
                <input
                  id="wpCardHolder"
                  type="text"
                  className="wpInput"
                  autoComplete="cc-name"
                  placeholder="Name as it appears on the card"
                  value={cardHolder}
                  onChange={(e) => setCardHolder(e.target.value)}
                  disabled={isBusy}
                />
              </div>

              <div className="wpField">
                <label className="wpLabel">Card number</label>
                <section id="card-pan" className="wpWpField" />
              </div>

              <div className="wpFieldRow">
                <div className="wpField">
                  <label className="wpLabel">Expiry (MM/YY)</label>
                  <section id="card-expiry" className="wpWpField" />
                </div>
                <div className="wpField">
                  <label className="wpLabel">CVV</label>
                  <section id="card-cvv" className="wpWpField" />
                </div>
              </div>

              {(message || sdkError) && (
                <div className="wpAlert" role="alert">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{sdkError || message}</span>
                </div>
              )}

              <button
                className="wpPayBtn"
                type="submit"
                disabled={isBusy || !sdkReady}
                style={{ backgroundColor: BRAND_GREEN }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ marginRight: 8 }}
                  aria-hidden="true"
                >
                  <path
                    d="M6 10V8a6 6 0 1112 0v2m-9 0h6a3 3 0 013 3v6a3 3 0 01-3 3H9a3 3 0 01-3-3v-6a3 3 0 013-3z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {isBusy ? "Processing…" : `Pay ${fmt(amount)}`}
              </button>

              <div className="wpSecondaryRow">
                <button
                  type="button"
                  className="wpClearBtn"
                  id="clear"
                  disabled={isBusy}
                >
                  Clear card details
                </button>
                <button
                  type="button"
                  className="wpCancelBtn"
                  onClick={closeModal}
                  disabled={isBusy}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>

          {/* Trust footer */}
          <div className="wpTrust">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 10V8a6 6 0 1112 0v2m-9 0h6a3 3 0 013 3v6a3 3 0 01-3 3H9a3 3 0 01-3-3v-6a3 3 0 013-3z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>
              Payments are encrypted and processed by Worldpay. Your card
              details never touch our servers.
            </span>
          </div>
        </div>

        {/* Processing overlay */}
        {isBusy && (
          <div className="wpOverlay">
            <div className="wpOverlayContent">
              <div className="wpSpinner large" />
              <h3>Processing your payment</h3>
              <p>Please don't close or refresh this window.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CardBadge({ text }) {
  return <span className="wpCardBadge">{text}</span>;
}
